import { err, ok, Result } from 'neverthrow';
import {
  GraphError,
  IToolResolver,
  ResolutionOrder,
  AgentItem,
  AgentNode,
} from './types';
import { ParseCtxFor } from './eraql-expr-checker';
import { EraQL } from '@cotera/era';
import { DocumentEditor } from '../document';
import { BuildExplorer } from '../commit/build-explorer';

export const resolveAgent = (
  node: AgentNode,
  opts: {
    readonly toolResolver: IToolResolver;
    readonly resolutionOrder: ResolutionOrder;
    readonly ctxFor: ParseCtxFor;
  }
): Result<AgentItem, { err: GraphError; partial: null }> => {
  // Create EditorState to extract mentions (tools)
  // Note: We use an empty graph here since we're during resolution and don't need recursive extraction
  const editorState = new DocumentEditor.EditorState(node.editorState);
  const mentions = editorState.getMentions(BuildExplorer.empty());

  // Extract tool mentions from editorState
  // The mention ID is the tool key (e.g., "google/search")
  const toolMentions = mentions
    .filter((mention) => mention.type === 'tool')
    .map((mention) => mention.referenceId ?? mention.id);

  // Validate that all mentioned tools exist using the tool resolver
  const validatedTools: string[] = [];
  for (const toolKey of toolMentions) {
    if (!opts.toolResolver.isToolAvailable(toolKey)) {
      const e: GraphError = {
        t: 'ffi-tool-not-found',
        type: toolKey,
        key: toolKey,
      };
      return err({ err: e, partial: null });
    }
    validatedTools.push(toolKey);
  }

  const outputTyParsed = EraQL.parseTy(node.outputTy);

  if (outputTyParsed.isErr()) {
    const e: GraphError = {
      t: 'eraql-errors',
      errs: outputTyParsed.error,
      source: node.outputTy,
    };
    return err({ err: e, partial: null });
  }

  const res: AgentItem = {
    t: 'agent',
    agentNode: node,
    availableToolSymbols: validatedTools,
    editorState: node.editorState,
    output: outputTyParsed.value,
    model: node.model,
    schedule: node.schedule,
  };

  return ok(res);
};
